import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ApiClientError, ICalendarAuth, ResponseWithStatus } from '@gettactic/api';

const MAX_RETRIES = 3;

// A 4xx (e.g. not being allowed to check another user) won't change on retry
const shouldRetry = (failureCount: number, error: unknown) => {
  if (error instanceof ApiClientError && error.response?.status < 500) {
    return false;
  }
  return failureCount < MAX_RETRIES;
};

export const useOpenWithIntegrationCheck = ({
  enabled = false,
  refetchInterval = 1000,
  asUserId,
  apiClient
}: {
  enabled?: boolean;
  refetchInterval?: number;
  /** Checks this user instead of the current one; requires being able to book for them. */
  asUserId?: string;
  apiClient: {
    getMicrosoftPermissions: (asUserId?: string) => Promise<ResponseWithStatus<ICalendarAuth>>
  }
}) => {
  const { refetch, isRefetching } = useQuery({
    queryKey: ['calendar-auth', asUserId ?? null],
    queryFn: () => apiClient.getMicrosoftPermissions(asUserId),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval,
    retry: shouldRetry
  });

  const getShouldRequestDelegatedAccess = useCallback(async () => {
    if(!enabled) {
      return false;
    }

    const freshData = await refetch();
    const freshPermissions = freshData.data?.result;

    if(!freshPermissions) {
      return false;
    }

    return freshPermissions.requires_delegated_access && !freshPermissions.has_delegated_access;
  }, [enabled, refetch]);

  return {
    getShouldRequestDelegatedAccess,
    isRefetching
  }
};
